import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { usePortal } from '../PortalContext';
import { getUdaThemeById } from '../udaThemes';
import { getLandmarksForSlug, landmarkImage } from '../udaLandmarks';
import { SectionHeading } from './SectionHeading';

// Lazy: maplibre-gl is ~800 KB and this panel is below the fold. A static import
// would put it in the portal's first-paint bundle for every visitor.
const LocationMap = lazy(() => import('./LocationMap'));

interface GalleryImage {
  imge_id: number;
  imge_url_tx: string;
  imge_nm?: string;
  dscn_tx?: string;
  /** 'video' | 'image'; null on rows written before video uploads were supported. */
  fle_typ_cd?: string | null;
}

/** Gallery rows can now be videos. Prefer the stored type; older rows have none. */
const isVideoItem = (img: GalleryImage) =>
  String(img.fle_typ_cd || '').toLowerCase().startsWith('video') ||
  /\.(mp4|webm|ogv|ogg|mov)(\?|#|$)/i.test(img.imge_url_tx || '');

/**
 * Fallback centre for the state-level portal, which is not a UDA and so has no
 * coordinates of its own: the Amaravati capital region. This is a map centre,
 * not a claim about where any office is.
 */
const AP_CAPITAL = { lat: 16.5062, lng: 80.6480, label: 'Andhra Pradesh' };

interface PanelHeaderProps {
  label: string;
  count: number;
  index: number;
  prevLabel: string;
  nextLabel: string;
  onPrev: () => void;
  onNext: () => void;
}

// NOTE: no opacity modifiers on the accent (`bg-[var(--portal-accent)]/10`).
// Tailwind 3.4 cannot compute alpha from a CSS variable and emits NO rule at all
// for that class, so the style would silently disappear. Only full-strength
// var() colors are used here; tints come from fixed hex values.
const navBtn =
  'w-7 h-7 rounded border border-gray-300 dark:border-white/15 bg-white dark:bg-white/[0.06] text-[var(--portal-accent)] ' +
  'flex items-center justify-center hover:bg-[var(--portal-accent)] hover:text-white hover:border-transparent transition-colors ' +
  'disabled:opacity-40 disabled:hover:bg-white dark:disabled:hover:bg-white/[0.06] disabled:hover:text-[var(--portal-accent)] disabled:hover:border-gray-300 dark:disabled:hover:border-white/15';

/* Panel shell + header bar, shared by the three gallery panels. Kept in one
   place so the light/dark pair stays consistent across them. */
const panelShell =
  'stagger-child bg-white dark:bg-white/[0.04] border border-gray-200 dark:border-white/10 rounded-lg overflow-hidden flex flex-col';
const panelBar =
  'flex items-center justify-between gap-2 bg-[#F0F8F2] dark:bg-white/[0.05] border-b border-gray-200 dark:border-white/10 px-3 py-2';
const panelTitle =
  'text-xs font-extrabold uppercase tracking-wide text-[#222222] dark:text-white border-b-2 border-[var(--portal-accent)] pb-0.5';
/* Media wells sit behind photos/videos, so they only show while loading or
   when a panel is empty. */
const mediaWell = 'bg-[#F7F7F7] dark:bg-[#12151c]';

/**
 * Panel chrome: a labelled header bar with the position counter and ‹ › controls.
 * Declared at module scope, not inside GallerySection — a component defined in a
 * render body is a new type on every render and remounts its subtree.
 */
const PanelHeader: React.FC<PanelHeaderProps> = ({ label, count, index, prevLabel, nextLabel, onPrev, onNext }) => (
  <div className={panelBar}>
    <div className="flex items-center gap-2 min-w-0">
      <span className={panelTitle}>
        {label}
      </span>
      {count > 0 && (
        <span className="text-[11px] text-[#666666] dark:text-white/50 whitespace-nowrap">{index + 1} / {count}</span>
      )}
    </div>
    <div className="flex items-center gap-1.5">
      <button type="button" onClick={onPrev} disabled={count < 2} aria-label={prevLabel} className={navBtn}>
        <i className="ri-arrow-left-s-line text-lg leading-none" />
      </button>
      <button type="button" onClick={onNext} disabled={count < 2} aria-label={nextLabel} className={navBtn}>
        <i className="ri-arrow-right-s-line text-lg leading-none" />
      </button>
    </div>
  </div>
);

export const GallerySection: React.FC = () => {
  const { t } = useTranslation();
  const { ref, isRevealed } = useScrollReveal({ threshold: 0.1 });
  const { siteId, publicApi } = usePortal();
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [landmarkImages, setLandmarkImages] = useState<GalleryImage[]>([]);

  useEffect(() => {
    setLoading(true);
    publicApi.get(`/gallery/images/images_gallery?siteId=${siteId}`)
      .then(res => {
        const data = res.data?.data || [];
        setImages(data.filter((img: any) => img.imge_url_tx));
      })
      .catch(() => setImages([]))
      .finally(() => setLoading(false));
  }, [siteId]);

  // Build a context-aware landmark fallback (DTCP → state landmarks, UDA → its
  // own). Each tile shows the real Wikipedia photo of that landmark; the keyword
  // image is only used if a landmark's Wikipedia page has no usable picture.
  useEffect(() => {
    const theme = getUdaThemeById(siteId); // null for DTCP / unknown
    const lms = getLandmarksForSlug(theme?.slug).slice(0, 8);
    // Show keyword images immediately, then upgrade to real Wikipedia photos.
    setLandmarkImages(lms.map((lm, i) => ({
      imge_id: -(i + 1), imge_url_tx: landmarkImage(lm.tag, i + 1), imge_nm: lm.name,
    })));

    const titles = lms.map(l => l.wiki).filter(Boolean);
    if (titles.length === 0) return;
    let cancelled = false;
    const url = `https://en.wikipedia.org/w/api.php?action=query&format=json&origin=*`
      + `&prop=pageimages&piprop=thumbnail&pithumbsize=800&redirects=1`
      + `&titles=${titles.map(encodeURIComponent).join('%7C')}`;
    fetch(url)
      .then(r => (r.ok ? r.json() : null))
      .then((d: any) => {
        if (cancelled || !d?.query?.pages) return;
        const norm: Record<string, string> = {};
        (d.query.normalized || []).forEach((n: any) => { norm[n.from] = n.to; });
        const redir: Record<string, string> = {};
        (d.query.redirects || []).forEach((rr: any) => { redir[rr.from] = rr.to; });
        const thumbByTitle: Record<string, string> = {};
        Object.keys(d.query.pages).forEach((k) => {
          const p = d.query.pages[k];
          if (p?.thumbnail?.source) thumbByTitle[p.title] = p.thumbnail.source;
        });
        const resolve = (title: string) => {
          let tt = norm[title] || title;
          tt = redir[tt] || tt;
          return thumbByTitle[tt];
        };
        setLandmarkImages(lms.map((lm, i) => ({
          imge_id: -(i + 1),
          imge_url_tx: (lm.wiki && resolve(lm.wiki)) || landmarkImage(lm.tag, i + 1),
          imge_nm: lm.name,
        })));
      })
      .catch(() => {}); // keep keyword fallback
    return () => { cancelled = true; };
  }, [siteId]);

  // Images and videos live in the same table and arrive in one response; the
  // section shows them as two separate "folders", so they are split here.
  const videoItems = images.filter(isVideoItem);
  const imageItems = images.filter(img => !isVideoItem(img));
  // Landmark photos keep the Images panel populated on a site that has not
  // uploaded anything yet. There is no equivalent filler for videos — an empty
  // Videos panel says so instead of inventing content.
  const displayImages = imageItems.length > 0 ? imageItems.slice(0, 12) : landmarkImages;

  // One item visible per panel, stepped with the ‹ › controls in its header.
  const [imgIdx, setImgIdx] = useState(0);
  const [vidIdx, setVidIdx] = useState(0);
  // Reset when the underlying list changes (site switch, or the fetch landing)
  // so the index can never point past the end of a shorter list.
  useEffect(() => { setImgIdx(0); }, [siteId, displayImages.length]);
  useEffect(() => { setVidIdx(0); }, [siteId, videoItems.length]);

  const step = (
    setter: React.Dispatch<React.SetStateAction<number>>,
    length: number,
    delta: number
  ) => setter(i => (length > 0 ? (i + delta + length) % length : 0));

  const currentImage = displayImages[imgIdx];
  const currentVideo = videoItems[vidIdx];

  // Where "here" is. On a UDA subdomain (suda.*, ruda.*) the theme carries that
  // authority's own coordinates and slug; the state-level portal has no UDA, so
  // the map falls back to the capital region with no boundary drawn.
  const theme = getUdaThemeById(siteId);
  const mapLat = theme?.lat ?? AP_CAPITAL.lat;
  const mapLng = theme?.lng ?? AP_CAPITAL.lng;
  const mapLabel = theme?.name ?? AP_CAPITAL.label;
  const mapAccent = theme?.accent ?? '#1E8E3E';

  // Full screen puts the whole location card — header included — up as the
  // fullscreen element, so the exit control stays reachable. The map inside
  // re-frames itself on the resulting resize (see LocationMap's ResizeObserver).
  const locationCard = useRef<HTMLDivElement>(null);
  const [isFullScreen, setIsFullScreen] = useState(false);
  useEffect(() => {
    const onChange = () => setIsFullScreen(document.fullscreenElement === locationCard.current);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);
  const toggleFullScreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen?.();
    } else {
      locationCard.current?.requestFullscreen?.();
    }
  };

  return (
    <section
      ref={ref as React.RefObject<HTMLElement>}
      className={`scroll-reveal portal-section portal-surface portal-rule ${isRevealed ? 'revealed' : ''}`}
    >
      <div className="reveal-content portal-container">
        <SectionHeading
          eyebrow="Gallery"
          title={t('gallery.title')}
          subtitle="Landmarks, projects and events from across the Authority's jurisdiction."
        />
      {/* Images, Videos and Map sit in one row as three equal "folder" panels.
          On narrow screens they stack; from md up they share a single line. */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
        {/* IMAGES */}
        <div className={panelShell}>
          <PanelHeader
            label={t('gallery.images')}
            count={displayImages.length}
            index={imgIdx}
            prevLabel={t('gallery.prev')}
            nextLabel={t('gallery.next')}
            onPrev={() => step(setImgIdx, displayImages.length, -1)}
            onNext={() => step(setImgIdx, displayImages.length, 1)}
          />

          <div className={`relative aspect-[16/10] ${mediaWell}`}>
            {loading ? (
              <div className="absolute inset-0 flex items-center justify-center animate-pulse text-[#9AA3AF]">
                <i className="ri-image-line text-3xl opacity-50" />
              </div>
            ) : currentImage ? (
              <>
                <img
                  key={currentImage.imge_id}
                  src={currentImage.imge_url_tx}
                  alt={currentImage.imge_nm || currentImage.dscn_tx || ''}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.visibility = 'hidden';
                  }}
                />

                {(currentImage.imge_nm || currentImage.dscn_tx) && (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/35 to-transparent px-3.5 pt-8 pb-3 pointer-events-none">
                    <p className="text-white text-[12.5px] font-semibold leading-tight line-clamp-2 drop-shadow-[0_1px_4px_rgba(0,0,0,0.8)]">
                      {currentImage.imge_nm || currentImage.dscn_tx}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-center text-[#999999] dark:text-white/40">
                <div>
                  <i className="ri-image-line text-3xl block text-[#1E8E3E]/30" />
                  <p className="text-xs mt-1">{t('gallery.noImages')}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* VIDEOS */}
        <div className={panelShell}>
          <PanelHeader
            label={t('gallery.videos')}
            count={videoItems.length}
            index={vidIdx}
            prevLabel={t('gallery.prev')}
            nextLabel={t('gallery.next')}
            onPrev={() => step(setVidIdx, videoItems.length, -1)}
            onNext={() => step(setVidIdx, videoItems.length, 1)}
          />

          <div className="relative aspect-[16/10] bg-black">
            {currentVideo ? (
              <>
                <video
                  key={currentVideo.imge_id}
                  src={currentVideo.imge_url_tx}
                  controls
                  preload="metadata"
                  className="w-full h-full object-contain bg-black"
                />

                {(currentVideo.imge_nm || currentVideo.dscn_tx) && (
                  <div className="absolute inset-x-0 bottom-10 bg-gradient-to-t from-black/75 via-black/30 to-transparent px-3 pt-6 pb-2 pointer-events-none">
                    <p className="text-white text-xs font-medium leading-tight line-clamp-2">
                      {currentVideo.imge_nm || currentVideo.dscn_tx}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className={`absolute inset-0 flex items-center justify-center text-center text-[#999999] dark:text-white/40 ${mediaWell}`}>
                <div>
                  <i className="ri-video-line text-3xl block text-[#1E8E3E]/30" />
                  <p className="text-xs mt-1">
                    {t('gallery.noVideos')}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* OUR LOCATION */}
        <div
          ref={locationCard}
          className={panelShell}
        >
          <div className={panelBar}>
            <span className={`${panelTitle} truncate`}>
              {mapLabel}
            </span>
            <button
              type="button"
              onClick={toggleFullScreen}
              title={isFullScreen ? t('location.exitFullScreen') : t('location.fullScreen')}
              className="text-[11px] font-medium text-[var(--portal-accent)] hover:underline whitespace-nowrap flex items-center gap-1"
            >
              <i className={isFullScreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'} />
              {isFullScreen ? t('location.exitFullScreen') : t('location.fullScreen')}
            </button>
          </div>
          {/* aspect-[16/10] matches the Images/Videos panels so all three align
              on one line; in fullscreen, flex-1 lets the map fill the screen. */}
          <div className={`relative ${mediaWell} ${isFullScreen ? 'flex-1' : 'aspect-[16/10]'}`}>
            <Suspense
              fallback={
                <div className="absolute inset-0 flex items-center justify-center text-[#999999] dark:text-white/40">
                  <i className="ri-map-pin-line text-3xl text-[#1E8E3E]/30" />
                </div>
              }
            >
              <LocationMap
                lat={mapLat}
                lng={mapLng}
                slug={theme?.slug}
                district={theme?.district}
                accent={mapAccent}
                label={mapLabel}
              />
            </Suspense>
          </div>
        </div>
      </div>
      </div>
    </section>
  );
};
